/**
 * Front design write-ups — the design's own texts as REAL objects.
 *
 * History: the 2026-11-04 ghost layer rendered the blank front's
 * write-ups as light-grey, non-selectable placeholder objects
 * (ghost: true). Princewill rejected that twice over: first because
 * the write-ups were faint and NOT DRAGGABLE ("I can't move it to
 * the direction I want them to be — so I need that design"), then,
 * when the fix kept a faint dummy MICR + samples riding the real
 * field objects, because faint placeholders are wrong as a concept:
 * the faint date "is not supposed to be there", a blank check for
 * handwriting must show NONE of the faint things, and when he
 * deletes something it must stay deleted, never replaced by a faint
 * stand-in.
 *
 * The rule now (2026-11-04, final):
 *  - The DESIGN's write-ups — the bank name/address block and the
 *    account-holder name/address — are real fabric objects: solid
 *    ink, selectable, draggable, registered in the front
 *    position/arrangement system, and they PRINT with the design.
 *    Typed bank/holder values win over the design's texts.
 *  - Transaction fields (date, payee, amount, amount-in-words,
 *    memo, MICR numbers) render ONLY what is typed. Nothing typed =
 *    the object carries empty text; the check shows just the
 *    design's printed labels and lines. No samples, no stand-ins.
 *  - The ghost system is REMOVED, not reduced: no ghost objects are
 *    created anywhere on the front — no ghost date, no dummy MICR
 *    ghost, no ghost captions, no sample texts. Exports therefore
 *    need no hiding pass: capture is a plain capture, and the
 *    typed-data blank rule (empty amount prints blank, empty MICR
 *    segments print nothing, a fully blank check prints no MICR
 *    line and no captions) is enforced by the render itself.
 */

import { bankBlockAddressText } from './addressBlocks';

/**
 * The design's own write-up texts for the blocks that belong to the
 * DESIGN (its identity furniture), used while the user has not typed
 * their own bank/holder details. Transaction fields have NO design
 * texts — they stay empty until typed.
 */
export const DESIGN_WRITEUP_TEXT = {
    bankName: 'Your Bank Name',
    bankAddress: '123 Main Street\nAnytown, ST 12345',
    holderName: 'Your Name',
    holderAddress: '456 Oak Avenue, Apt 7\nSpringfield, ST 67890'
} as const;

/**
 * Compose the bank block's two text parts from typed data and the
 * design's write-ups, part by part: a typed bank name wins for the
 * name line (the renderer adds its bank glyph), and the address part
 * carries the typed address lines, the typed bank name's address
 * when only a name was typed, or the design's address otherwise.
 * The routing line prints only when a routing number exists — a
 * design write-up never invents one.
 */
export function designBankBlock(input: {
    bankName?: string;
    bankAddress?: string;
    routingNumber?: string;
}): { nameText: string; addressText: string } {
    const typedName = (input.bankName || '').trim();
    const typedAddress = (input.bankAddress || '').trim();
    const nameText = typedName || DESIGN_WRITEUP_TEXT.bankName;
    const baseAddress = typedAddress || DESIGN_WRITEUP_TEXT.bankAddress;
    const addressText = bankBlockAddressText(baseAddress, input.routingNumber);
    return { nameText, addressText };
}

/**
 * Capture the canvas for export. Ghosts are abolished, so there is
 * nothing to hide: the render itself guarantees that unwritten
 * fields carry no text and a blank check carries no MICR line. The
 * helper stays as the single capture entry point the export paths
 * already call, so preview and export can never drift apart.
 */
export function captureCanvasWithoutGhosts(
    canvas: any,
    opts?: { format?: string; multiplier?: number }
): string {
    return canvas.toDataURL({ format: opts?.format ?? 'png', multiplier: opts?.multiplier ?? 1 });
}
